import React from 'react';
import classNames from 'classnames';

import Data from 'data';
import Store from 'store';
import Locale from 'data/locale';

import { selectStats } from 'logic/stats';
import { Color } from 'common';
import { StatTooltip } from 'components/stats/Stats';
import Tooltip from 'ui/tooltip';
import { D2SkillTooltip } from 'tooltips';
import { useCtrl } from 'ui/modifiers';
import NumberInput from 'ui/number';
import Slider from 'ui/slider';
import { profileSelectSkills } from 'logic/stateReducers';

import './SkillTree.scss';

const _L = Locale("ui-character");

const GridLeft = 15;
const GridTop = 14;
const GridRow = 68;
const GridCol = 69;
const GridPage = 233;

const GridLeftHD = 49;
const GridTopHD = 56;
const GridColHD = 97;
const GridRowHD = 66;

export function checkPrereq(id, skills) {
  function check(id) {
    const sk = Data.skills[id];
    return !sk || skills[sk.id];
  }
  const skill = Data.skills[id];
  return check(skill.reqskill1) && check(skill.reqskill2) && check(skill.reqskill3);
}
export const questPoints = quests => quests?.reduce((sum, q) => {
  if (q.denofevil) sum += 1;
  if (q.radamentslair) sum += 1;
  if (q.thefallenangel) sum += 2;
  return sum;
}, 0) || 0;

export const gridPos = (page, row, col, hd) => {
  if (hd && page < 0) {
    return {
      left: GridLeftHD + (col - 1) * GridColHD,
      top: GridTopHD + (row - 1) * GridRowHD,
    };
  } else {
    if (page < 0) page = 3;
    return {
      left: GridLeft + (3 - page) * GridPage + (col - 1) * GridCol,
      top: GridTop + (row - 1) * GridRow + (row >= 5 ? 1 : 0),
    };
  }
}


function SkillLevelTip({ id }) {
  const stats = Store.useSelector(selectStats).player;
  const stat = {
    name: _L`Level`,
    value: stats => stats.skills[id],
    tooltip: [
      `{sources:skills.${id}}`,
    ],
  };
  return <StatTooltip stat={stat} stats={stats} skills unit="player"/>;
}

export function SkillButton({ id, stats, onHover, onPressed, canLevel, bypassLevel, onChange, recording, noPage, hd, ...props }) {
  const [editing, setEditing] = React.useState(false);
  const [pressed, setPressed] = React.useState(false);
  const ctrlKey = useCtrl();

  const skill = Data.skills[id];
  const desc = skill && Data.skillDesc[skill.skilldesc];

  const level = stats.base_skills[id] || 0;
  const canLevelThis = canLevel && checkPrereq(id, stats.base_skills) && (bypassLevel || stats.level >= skill.reqlevel + level);

  React.useEffect(() => {
    if (recording || !onChange) setEditing(false);
  }, [recording, onChange]);

  const onFocus = React.useCallback(() => !recording && !!onChange && (level || canLevelThis) && setEditing(true),
    [level, canLevelThis, recording, onChange]);
  const onBlur = React.useCallback(() => setEditing(false), []);
  const inputRef = React.useRef();
  React.useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, [editing]);

  const hasDepends = React.useMemo(() => Object.entries(stats.base_skills).some(([sid, level]) => {
    if (!level) return false;
    const skill = Data.skills[sid];
    const nid = parseInt(id);
    return (skill.reqskill1 === nid || skill.reqskill2 === nid || skill.reqskill3 === nid);
  }), [id, stats]);

  props = {...props};

  const events = {};
  events.onClick = React.useCallback(() => {
    if (level < 20 && canLevelThis) {
      onChange(id, level + 1);
    }
  }, [id, onChange, level, canLevelThis]);
  events.onContextMenu = React.useCallback(e => {
    e.preventDefault();
    if (level > 0 && !recording) {
      if (level === 1 && hasDepends) return;
      onChange(id, level - 1);
    }
  }, [id, onChange, level, hasDepends, recording]);
  props.onMouseOver = React.useCallback(() => onHover(id), [id, onHover]);
  props.onMouseOut = React.useCallback(() => onHover(null), [onHover]);
  
  events.onMouseDown = React.useCallback(() => {
    if (level < 20 && canLevelThis) {
      setPressed(true);
    }
  }, [level, canLevelThis]);
  props.onDragStart = React.useCallback(e => e.preventDefault(), []);
  React.useEffect(() => {
    if (!onPressed) {
      return;
    } if (pressed) {
      onPressed(id);
      const handler = () => setPressed(false);
      document.addEventListener('mouseup', handler);
      return () => document.removeEventListener('mouseup', handler);
    } else {
      onPressed(null);
    }
  }, [pressed, onPressed, id]);

  if (onChange) {
    Object.assign(props, events);
  }

  const onInput = React.useCallback(value => {
    if (value > 20) value = 20;
    if (hasDepends && value < 1) value = 1;
    onChange(id, value);
  }, [id, onChange, hasDepends]);

  if (!desc) return null;
  const page = desc.skillpage, row = desc.skillrow, col = desc.skillcolumn;
  if (!page || !row || !col) return null;

  const totalLevel = (ctrlKey ? level : (stats.skills[id] || 0));

  return (
    <div className="d2p-SkillTreeButton d2p-no-link" style={gridPos(noPage ? -1 : page, row, col, hd)}>
      <Tooltip tooltip={<SkillLevelTip id={id}/>} disabled={!stats.skills[id]} align={editing ? "left" : null}>
        <div className="d2p-skill-level" onClick={onFocus}>
          {!!(!editing && hd && totalLevel) && <span className={`d2-color-${totalLevel > level ? Color.BLUE : Color.WHITE}`}>{totalLevel}</span>}
          {!!editing && <NumberInput min={hasDepends ? 1 : 0} max={20} value={level} onChange={onInput} onBlur={onBlur} ref={inputRef}/>}
        </div>
      </Tooltip>
      <D2SkillTooltip id={id} canLevel={canLevelThis} full>
        <div className="d2p-skill-icon" {...props}/>
      </D2SkillTooltip>
    </div>
  );
}

export const skillTreeNames = {
  ama: [
    ['StrSklTree10', 'StrSklTree11', 'StrSklTree4'],
    ['StrSklTree8', 'StrSklTree9', 'StrSklTree4'],
    ['StrSklTree6', 'StrSklTree7', 'StrSklTree4'],
  ],
  sor: [
    ['StrSklTree25', 'StrSklTree5'],
    ['StrSklTree24', 'StrSklTree5'],
    ['StrSklTree23', 'StrSklTree5'],
  ],
  nec: [
    ['StrSklTree19'],
    ['StrSklTree17', 'StrSklTree18', 'StrSklTree5'],
    ['StrSklTree16', 'StrSklTree5'],
  ],
  pal: [
    ['StrSklTree15', 'StrSklTree4'],
    ['StrSklTree14', 'StrSklTree13'],
    ['StrSklTree12', 'StrSklTree13'],
  ],
  bar: [
    ['StrSklTree21', 'StrSklTree4'],
    ['StrSklTree21', 'StrSklTree22'],
    ['StrSklTree20'],
  ],
  dru: [
    ['StrSklTree26'],
    ['StrSklTree27', 'StrSklTree28'],
    ['StrSklTree29'],
  ],
  ass: [
    ['StrSklTree30'],
    ['StrSklTree31', 'StrSklTree32'],
    ['StrSklTree33', 'StrSklTree34'],
  ],
};

export function SkillSlider({ recording, setRecording, setHighlight }) {
  const dispatch = Store.useDispatch();
  const skillProgression = Store.useProfile(profile => profile.skillProgression);
  const skillPos = Store.useProfile(profile => profile.skillPos) || 0;

  const onRecord = React.useCallback(() => {
    if (recording) {
      setRecording(false);
    } else {
      setRecording(true);
      dispatch.setProfile(profile => {
        const skills = profileSelectSkills(profile);
        return {...profile, skills, skillProgression: [], skillPos: 0};
      });
    }
  }, [recording, setRecording, dispatch]);

  const leftDisabled = recording || !skillProgression?.length || !skillPos;
  const rightDisabled = recording || !skillProgression?.length || skillPos >= skillProgression.length;
  const sliderDisabled = recording || !skillProgression?.length;

  const onSlider = React.useCallback(value => !recording && dispatch.setProfile(profile => {
    const length = profile.skillProgression?.length;
    if (!length) return profile;
    value = Math.min(length, Math.max(0, value));
    if (value === profile.skillPos) return profile;
    return {...profile, skillPos: value};
  }), [dispatch, recording]);
  const onLeft = React.useCallback(() => onSlider(skillPos - 1), [onSlider, skillPos]);
  const onRight = React.useCallback(() => onSlider(skillPos + 1), [onSlider, skillPos]);

  const lastPos = React.useRef(skillPos);
  React.useEffect(() => {
    const delta = skillPos - (lastPos.current || 0);
    lastPos.current = skillPos;
    if (setHighlight && !recording && skillProgression?.length && delta) {
      const id = skillProgression[delta < 0 ? skillPos : skillPos - 1];
      if (!id) return;
      setHighlight({id, delta});
      const timeout = setTimeout(() => setHighlight(null), 500);
      return () => {
        setHighlight(null);
        clearTimeout(timeout);
      };
    }
  }, [skillPos, skillProgression, recording, setHighlight]);

  return (
    <div className="d2p-SkillSlider">
      {!!setRecording && (
        <Tooltip tooltip={recording ? _L`Stop recording` : _L`Record skill progression`}>
          <div className={classNames("d2p-button d2p-record", {"d2p-active": recording})} onClick={onRecord}/>
        </Tooltip>
      )}
      <div className={classNames("d2p-button d2p-move d2p-left", {"d2p-disabled": leftDisabled})} onMouseDown={onLeft}/>
      <Slider min={0} max={skillProgression?.length || 0} step={1} value={skillPos} onChange={onSlider}
        disabled={sliderDisabled} className={classNames({"d2p-disabled": sliderDisabled})}/>
      <div className={classNames("d2p-button d2p-move d2p-right", {"d2p-disabled": rightDisabled})} onMouseDown={onRight}/>
    </div>
  );
}
